'use client';
import React, { useState, useRef, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import UploadZone from '@/components/ui/UploadZone';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import SuccessAnimation from '@/components/ui/SuccessAnimation';

interface ImageInfo {
  file: File;
  url: string;
  size: number;
  width: number;
  height: number;
}

interface CompressedResult {
  url: string;
  size: number;
  file: File;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getSavingsPercent(original: number, compressed: number): number {
  if (original === 0) return 0;
  return Math.max(0, Math.round(((original - compressed) / original) * 100));
}

export default function ImageCompressorTool() {
  const [image, setImage] = useState<ImageInfo | null>(null);
  const [quality, setQuality] = useState(80);
  const [compressed, setCompressed] = useState<CompressedResult | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewMode, setPreviewMode] = useState<'before' | 'after'>('before');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadImage = useCallback((file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      setError('Unsupported format. Please upload a JPG, PNG, or WebP image.');
      return;
    }
    setError(null);
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      setImage({ file, url, size: file.size, width: img.naturalWidth, height: img.naturalHeight });
      setCompressed(null);
    };
    img.onerror = () => {
      setError('Failed to load image. Please try a different file.');
    };
    img.src = url;
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadImage(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadImage(file);
  };

  const handleCompress = async () => {
    if (!image) return;
    setProcessing(true);
    setCompressed(null);
    setError(null);

    try {
      const options = {
        maxSizeMB: 100,
        initialQuality: quality / 100,
        useWebWorker: true,
        fileType: image.file.type as 'image/jpeg' | 'image/png' | 'image/webp',
        alwaysKeepResolution: true,
      };

      const compressedFile = await imageCompression(image.file, options);
      const url = URL.createObjectURL(compressedFile);
      setCompressed({ url, size: compressedFile.size, file: compressedFile });
      setPreviewMode('after');
    } catch (err) {
      console.error(err);
      setError('Compression failed. Please try a different image or quality setting.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!compressed || !image) return;
    const ext = image.file.type === 'image/png' ? 'png' : image.file.type === 'image/webp' ? 'webp' : 'jpg';
    const a = document.createElement('a');
    a.href = compressed.url;
    a.download = `compressed_${image.file.name.replace(/\.[^.]+$/, '')}.${ext}`;
    a.click();
  };

  const handleReset = () => {
    setImage(null);
    setCompressed(null);
    setError(null);
    setPreviewMode('before');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const savings = compressed && image ? getSavingsPercent(image.size, compressed.size) : 0;

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload zone */}
      {!image ? (
        <UploadZone
          onFiles={(files) => { const f = files[0]; if (f) loadImage(f); }}
          accept="image/jpeg,image/png,image/webp"
         
          title="Upload Image"
          subtitle="Drag & drop or click to browse"
          hint="Supports JPG, PNG, WebP"
          icon={
            <svg className="w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          }
        />
      ) : (
        <div className="space-y-5 animate-fade-in-up">
          {/* Before / After Preview */}
          <div className="rounded-2xl border border-gray-100 overflow-hidden bg-gray-50">
            {/* Toggle tabs */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setPreviewMode('before')}
                className={`btn-press flex-1 py-2.5 text-sm font-medium transition-colors ${
                  previewMode === 'before'
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600' :'text-gray-500 hover:text-gray-700'
                }`}
              >
                Original
              </button>
              <button
                onClick={() => setPreviewMode('after')}
                disabled={!compressed}
                className={`btn-press flex-1 py-2.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  previewMode === 'after' ?'bg-white text-blue-600 border-b-2 border-blue-600' :'text-gray-500 hover:text-gray-700'
                }`}
              >
                Compressed {compressed ? `(${savings}% saved)` : ''}
              </button>
            </div>

            {/* Preview image */}
            <div className="h-56 sm:h-72 flex items-center justify-center p-4 bg-[repeating-conic-gradient(#f0f0f0_0%_25%,transparent_0%_50%)_0_0/16px_16px] relative">
              {processing && (
                <div className="loading-overlay">
                  <LoadingSpinner size="lg" label="Compressing…" />
                </div>
              )}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={previewMode === 'after' && compressed ? compressed.url : image.url}
                alt={previewMode === 'after' ? 'Compressed preview' : 'Original preview'}
                className="max-h-full max-w-full object-contain rounded-lg shadow-sm"
              />
            </div>

            {/* Size label */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-white border-t border-gray-100">
              <span className="text-xs text-gray-500 truncate max-w-[180px]">{image.file.name}</span>
              <span className="text-xs font-semibold text-gray-700">
                {previewMode === 'after' && compressed
                  ? formatBytes(compressed.size)
                  : formatBytes(image.size)}
              </span>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-gray-500 text-xs mb-0.5">Original</p>
              <p className="font-bold text-gray-800 text-sm">{formatBytes(image.size)}</p>
            </div>
            <div className={`rounded-xl p-3 text-center transition-colors duration-300 ${compressed ? 'bg-blue-50' : 'bg-gray-50'}`}>
              <p className="text-gray-500 text-xs mb-0.5">Compressed</p>
              <p className={`font-bold text-sm ${compressed ? 'text-blue-600' : 'text-gray-400'}`}>
                {compressed ? formatBytes(compressed.size) : '—'}
              </p>
            </div>
            <div className={`rounded-xl p-3 text-center transition-colors duration-300 ${compressed ? 'bg-green-50' : 'bg-gray-50'}`}>
              <p className="text-gray-500 text-xs mb-0.5">Saved</p>
              <p className={`font-bold text-sm ${compressed ? 'text-green-600' : 'text-gray-400'}`}>
                {compressed ? `${savings}%` : '—'}
              </p>
            </div>
          </div>

          {/* Savings progress bar */}
          {compressed && (
            <div className="animate-fade-in-up">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span className="flex items-center gap-1.5">
                  <SuccessAnimation size="sm" />
                  File size reduction
                </span>
                <span>{savings}% smaller</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-700"
                  style={{ width: `${savings}%` }}
                />
              </div>
            </div>
          )}

          {/* Quality slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-gray-800 text-sm">Quality</label>
              <span className="text-blue-600 font-bold text-sm bg-blue-50 px-2.5 py-0.5 rounded-lg">{quality}%</span>
            </div>
            <input
              type="range"
              min={10}
              max={100}
              step={5}
              value={quality}
              onChange={(e) => { setQuality(Number(e.target.value)); setCompressed(null); setPreviewMode('before'); }}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: '#2563EB' }}
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1.5">
              <span>10 — Smaller file</span>
              <span>100 — Best quality</span>
            </div>
          </div>

          {/* Compress button */}
          <button
            onClick={handleCompress}
            disabled={processing}
            className="btn-action w-full py-3.5 rounded-xl text-sm"
          >
            {processing ? (
              <>
                <LoadingSpinner size="sm" variant="white" />
                Compressing…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859M12 3v8.25m0 0l-3-3m3 3l3-3" />
                </svg>
                Compress Image
              </>
            )}
          </button>

          {/* Download + Reset */}
          {compressed && (
            <div className="flex gap-3 animate-fade-in-up">
              <button
                onClick={handleDownload}
                className="btn-action flex-1 py-3 rounded-xl text-sm"
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download Compressed
              </button>
              <button
                onClick={handleReset}
                className="btn-press px-4 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                Reset
              </button>
            </div>
          )}

          {!compressed && (
            <button
              onClick={handleReset}
              className="btn-press w-full py-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              Remove & Upload Another
            </button>
          )}
        </div>
      )}
    </div>
  );
}
